import type {
  StBrandTone,
  StBudgetField,
  StBudgetFieldId,
  StConnectionItem,
  StDoctorItem,
  StOfficeHour,
  StPracticeProfileField,
  StPracticeProfileFieldId,
  StToggleItem,
} from './types';

// ── Budget & Guardrails ──────────────────────────────────────────────────────
export const ST_BUDGET_FIELDS: StBudgetField[] = [
  {
    id: 'monthly-max',
    label: 'Monthly Maximum Spend',
    hint: 'ELVA will never spend more than this in a calendar month across all campaigns.',
    suffix: '/month',
  },
  {
    id: 'daily-cap',
    label: 'Daily Spend Cap',
    hint: 'If total daily spend across all campaigns exceeds this, ELVA pauses until tomorrow.',
    suffix: '/day',
  },
  {
    id: 'campaign-max',
    label: 'Per-Campaign Maximum',
    hint: 'No single campaign can exceed this monthly budget, even if ELVA recommends increasing it.',
    suffix: '/campaign',
  },
  {
    id: 'alert-threshold',
    label: 'Alert Threshold',
    hint: "Get an alert if any single day's spend exceeds this amount.",
    suffix: '/day alert',
  },
];

export const ST_INITIAL_BUDGET_VALUES: Record<StBudgetFieldId, string> = {
  'monthly-max': '3500',
  'daily-cap': '150',
  'campaign-max': '1000',
  'alert-threshold': '200',
};

// ── Practice Profile ─────────────────────────────────────────────────────────
export const ST_PRACTICE_PROFILE_FIELDS: StPracticeProfileField[] = [
  { id: 'practice-name', label: 'Practice Name' },
  { id: 'phone-number', label: 'Phone Number' },
  { id: 'address', label: 'Address' },
  { id: 'website', label: 'Website' },
];

export const ST_INITIAL_PROFILE_VALUES: Record<StPracticeProfileFieldId, string> = {
  'practice-name': 'Smile Dental Downtown',
  'phone-number': '(905) 555-0123',
  address: '456 Main St, Brampton, ON L6Y IN2',
  website: 'smiledentaldowntown.com',
};

export const ST_SERVICES: string[] = [
  'General Dentistry',
  'Emergency Care',
  'Dental Implants',
  'Invisalign',
  'Teeth Whitening',
  'Cosmetic Veneers',
  'Pediatric Dentistry',
  'Sedation Dentistry',
  'Root Canals',
  'Crowns & Bridges',
];

export const ST_DOCTORS: StDoctorItem[] = [
  {
    id: 'dr-sarah-chen',
    name: 'Dr. Sarah Chen',
    roleLine: 'Lead Dentist - Implants, All-on-4, Sedation',
  },
  {
    id: 'dr-james-martinez',
    name: 'Dr. James Martinez',
    roleLine: 'General Dentist - Family, Cosmetic, Whitening',
  },
];

export const ST_OFFICE_HOURS: StOfficeHour[] = [
  { day: 'Mon', hour: '8a-6p', closed: false },
  { day: 'Tue', hour: '8a-6p', closed: false },
  { day: 'Wed', hour: '8a-8p', closed: false },
  { day: 'Thu', hour: '8a-6p', closed: false },
  { day: 'Fri', hour: '8a-5p', closed: false },
  { day: 'Sat', hour: '9a-3p', closed: false },
  { day: 'Sun', hour: 'Closed', closed: true },
];

// ── Connections ──────────────────────────────────────────────────────────────
export const ST_CONNECTIONS: StConnectionItem[] = [
  {
    id: 'google-ads',
    title: 'Google Ads',
    status: 'connected',
    detail: 'Account: Smile Dental Downtown (ID: 847-291-3847)',
  },
  {
    id: 'website',
    title: 'Website',
    status: 'connected',
    detail: 'smiledentaldowntown.com - Last crawled 6 hours ago',
  },
  {
    id: 'google-business-profile',
    title: 'Google Business Profile',
    status: 'connected',
    detail: 'Profile 85% complete - 245 reviews',
  },
  {
    id: 'search-console',
    title: 'Google Search Console',
    status: 'not-connected',
    detail: 'Connect to sync impressions, clicks, and ranking data.',
  },
];

// ── Target Area ──────────────────────────────────────────────────────────────
export const ST_EXCLUDED_ZIPS: string[] = ['L6P 3K2'];

// ── Autopilot Rules ──────────────────────────────────────────────────────────
export const ST_AUTOPILOT_LOW_RISK: StToggleItem[] = [
  {
    label: 'Adjust keyword bids',
    description:
      'Raise or lower bids based on conversion data to get the best cost per patient.',
    enabled: true,
  },
  {
    label: 'Pause underperforming keywords',
    description: 'Stop spending on keywords that get clicks but no bookings.',
    enabled: true,
  },
  {
    label: 'Shift budget between campaigns',
    description:
      'Move money from lower-performing campaigns to higher-performing ones within your total budget.',
    enabled: true,
  },
  {
    label: 'A/B test ad copy',
    description:
      'Create and test new ad headline/description variants to find what converts best.',
    enabled: true,
  },
];

export const ST_AUTOPILOT_HIGH_IMPACT: StToggleItem[] = [
  {
    label: 'Launch new campaigns automatically',
    description:
      'Let ELVA create and launch new campaigns when it finds strong opportunities - without asking first.',
    enabled: false,
  },
  {
    label: 'Increase campaign budgets',
    description:
      "Let ELVA raise a campaign's budget when it's performing well (still limited by your monthly maximum).",
    enabled: false,
  },
];

// ── Notifications ────────────────────────────────────────────────────────────
export const ST_NOTIFY_ABOUT: StToggleItem[] = [
  {
    label: 'Budget alerts',
    description: 'When daily spend exceeds your alert threshold.',
    enabled: true,
  },
  {
    label: 'Weekly performance summary',
    description:
      'Every Monday: how much you spent, how many patients booked, and your ROI.',
    enabled: true,
  },
  {
    label: 'New campaign suggestions',
    description: 'When ELVA identifies a new advertising opportunity.',
    enabled: true,
  },
  {
    label: 'Competitor changes',
    description:
      'When a competitor launches or pauses ads, changes their website, or gains a significant number of reviews.',
    enabled: true,
  },
];

export const ST_NOTIFY_HOW: StToggleItem[] = [
  {
    label: 'Email',
    description: 'dr.chen@smiledentaldowntown.com',
    enabled: true,
  },
  {
    label: 'SMS',
    description: '(905) 555-0199 - for urgent budget alerts only.',
    enabled: false,
  },
];

// ── Content Preferences ──────────────────────────────────────────────────────
export const ST_TOPICS_TO_AVOID: string[] = ['Competitor names', 'Price guarantees'];

export const ST_NEGATIVE_KEYWORDS: string[] = ['free dental', 'dental school', 'DIY'];

export const ST_BRAND_TONES: StBrandTone[] = [
  {
    id: 'friendly-warm',
    title: 'Friendly & Warm',
    description: 'Approachable, conversational, reassuring',
  },
  {
    id: 'professional',
    title: 'Professional',
    description: 'Clinical confidence, expertise-focused',
  },
  {
    id: 'casual-fun',
    title: 'Casual & Fun',
    description: 'Playful, lighthearted, community-oriented',
  },
];
